import { getActiveSessionManager } from '@bounded-sh/core';
import { setCurrentUser } from '../../global';
import { getPlatform } from '../../platform';
import { hasTurnkeyWallet, signAndSubmitTransactionWithTurnkey, signMessageWithTurnkey, signTransactionWithTurnkey, } from '../turnkey-signer-bridge';
// Web uses localStorage (WebSessionManager); React Native uses the configured
// ReactNativeSessionManager. getActiveSessionManager() picks the right one by
// whether the RN manager was configured (NOT `typeof window`, which is truthy on
// RN too and would wrongly route to localStorage). Same selector the core
// request path uses, so hosted Bounded Auth sessions persist + authenticate on BOTH.
const sessionManager = getActiveSessionManager;
const NO_WALLET = 'This session has no Turnkey wallet. Sign in again to provision the default ' +
    'embedded wallet, or check whether the app explicitly sets auth.wallets:false.';
// ---------------------------------------------------------------------------
// token / session helpers
// ---------------------------------------------------------------------------
function decodeJwt(token) {
    try {
        let b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        if (pad === 2)
            b64 += '==';
        else if (pad === 3)
            b64 += '=';
        // getPlatform().atob is RN-safe (global atob is undefined on Hermes/JSC).
        return JSON.parse(getPlatform().atob(b64));
    }
    catch (_a) {
        return {};
    }
}
function userFromIdToken(idToken, fallbackEmail) {
    const c = decodeJwt(idToken);
    const id = c['custom:userId'] || c.sub || c['custom:walletAddress'] || '';
    const email = (c.email || fallbackEmail || null);
    return {
        id,
        address: (c['custom:walletProvider'] === 'turnkey' && c['custom:walletAddress'] || null),
        evmAddress: null,
        email,
        provider: emailProviderSingleton,
    };
}
// ---------------------------------------------------------------------------
// provider
// ---------------------------------------------------------------------------
export class EmailAuthProvider {
    async login() {
        throw new Error('login() no longer opens inline email OTP. Use loginWithRedirect({ redirectUri }) ' +
            'or loginWithPopup({ redirectUri }) for hosted Bounded Auth.');
    }
    async restoreSession() {
        const sm = sessionManager();
        const session = await sm.getSession();
        if (!session)
            return null;
        const idToken = sm.getIdToken();
        if (!idToken)
            return null;
        const user = userFromIdToken(idToken);
        setCurrentUser(user);
        return user;
    }
    async logout() {
        await sessionManager().clearSession();
        setCurrentUser(null);
    }
    async signMessage(message) {
        if (!hasTurnkeyWallet())
            throw new Error(NO_WALLET);
        return signMessageWithTurnkey(message);
    }
    async signTransaction(transaction) {
        if (!hasTurnkeyWallet())
            throw new Error(NO_WALLET);
        return signTransactionWithTurnkey(transaction);
    }
    async signAndSubmitTransaction(transaction, feePayer) {
        if (!hasTurnkeyWallet())
            throw new Error(NO_WALLET);
        return signAndSubmitTransactionWithTurnkey(transaction, feePayer);
    }
    async getNativeMethods() { return {}; }
}
const emailProviderSingleton = new EmailAuthProvider();
export { emailProviderSingleton };
